// src/components/LoginModal.js
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  Dimensions
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Button, IconButton } from 'react-native-paper';
import { AVAQualisTheme } from '../theme/theme';
import { AVALIADORES, getLevelEmoji, getLevelDescription } from '../data/EstruturaOrganizacional';
import AuthService from '../services/authService';

const { height: windowHeight } = Dimensions.get('window');

const LoginModal = ({ visible, onClose, onLoginSuccess }) => {
  const [selectedAvaliador, setSelectedAvaliador] = useState(null);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [email, setEmail] = useState('');

  // Timer para reenvio de código
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // Reset ao fechar
  useEffect(() => {
    if (!visible) {
      setSelectedAvaliador(null);
      setShowCodeInput(false);
      setVerificationCode('');
      setEmail('');
      setResendCountdown(0);
    }
  }, [visible]);

  // Auto-submit quando atingir 5 dígitos
  useEffect(() => {
    if (verificationCode.length === 5 && !loading) {
      // Pequeno delay para dar feedback visual ao usuário
      const timer = setTimeout(() => {
        handleVerifyCode();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [verificationCode, loading]);

  // Solicitar código de verificação
  const handleRequestCode = async (avaliador) => {
    setLoading(true);
    try {
      const result = await AuthService.requestVerificationCode(avaliador.id);
      
      if (result.success) {
        setSelectedAvaliador(avaliador);
        setEmail(result.email);
        setShowCodeInput(true);
        setResendCountdown(60);
        setVerificationCode('');
        
        // Se houver código de desenvolvimento, mostrá-lo
        if (result.devCode) {
          console.log(`🔑 CÓDIGO DE DESENVOLVIMENTO: ${result.devCode}`);
          Alert.alert(
            '🔧 Modo Desenvolvimento',
            `Email não configurado.\n\nCódigo de verificação: ${result.devCode}\n\nEste código foi gerado mas não foi enviado por email.`,
            [{ text: 'OK' }]
          );
        } else if (result.emailError) {
          Alert.alert(
            '⚠️ Aviso',
            `O código foi gerado mas houve um problema ao enviar o email para ${result.email}.\n\nVerifique o console do servidor para o código.`,
            [{ text: 'OK' }]
          );
        } else {
          Alert.alert(
            '📧 Código Enviado',
            `Um código de verificação foi enviado para ${result.email}. Verifique sua caixa de entrada ou pasta de spam.`,
            [{ text: 'OK' }]
          );
        }
      } else {
        Alert.alert(
          '❌ Erro ao Enviar Código',
          result.error || 'Não foi possível enviar o código de verificação. Tente novamente.',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      console.error('Erro ao solicitar código:', error);
      Alert.alert(
        '❌ Erro', 
        'Erro ao solicitar código de verificação. Verifique sua conexão e tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Verificar código
  const handleVerifyCode = async () => {
    if (verificationCode.length !== 5) {
      Alert.alert('❌ Código Inválido', 'O código deve ter 5 dígitos.');
      return;
    }

    setLoading(true);
    try {
      const result = await AuthService.verifyCode(selectedAvaliador.id, verificationCode);
      
      if (result.success) {
        const expiresIn = result.expiresIn;
        const days = Math.floor(expiresIn / (60 * 60 * 24));
        
        onClose();
        onLoginSuccess();
        
        Alert.alert(
          '✅ Login Realizado com Sucesso',
          `Bem-vindo(a), ${result.avaliador.nome}!\n\nSua sessão permanecerá ativa por ${days} dias neste dispositivo.`,
          [{ text: 'Continuar' }]
        );
      } else {
        Alert.alert(
          '❌ Código Inválido',
          result.error || 'O código informado está incorreto ou expirou. Tente novamente.',
          [{ text: 'OK' }]
        );
        // Limpar o campo para nova tentativa
        setVerificationCode('');
      }
    } catch (error) {
      Alert.alert('❌ Erro', 'Erro ao verificar código.');
      setVerificationCode('');
    } finally {
      setLoading(false);
    }
  };

  // Reenviar código
  const handleResendCode = async () => {
    if (selectedAvaliador) {
      await handleRequestCode(selectedAvaliador);
    }
  };

  // Formatar nome do avaliador (apenas dois primeiros nomes)
  const formatAvaliadorName = (nome) => {
    const parts = nome.split(' ');
    return parts.slice(0, 2).join(' ');
  };

  // Handler para mudança do código com validação
  const handleCodeChange = (text) => {
    // Permitir apenas números
    const numericText = text.replace(/[^0-9]/g, '');
    setVerificationCode(numericText);
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardView}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {showCodeInput ? 'Verificação de Código' : 'Fazer Login'}
              </Text>
              <IconButton
                icon="close"
                size={24}
                onPress={onClose}
                style={styles.closeButton}
              />
            </View>

            {showCodeInput ? (
              // Tela de inserção de código
              <ScrollView 
                contentContainerStyle={styles.codeSection}
                showsVerticalScrollIndicator={false}
              >
                <MaterialCommunityIcons
                  name="shield-check"
                  size={80}
                  color="#A4C639"
                  style={styles.codeIcon}
                />

                <Text style={styles.codeTitle}>Digite o Código de Verificação</Text>
                
                <Text style={styles.codeDescription}>
                  Foi enviado um código de 5 dígitos para:
                </Text>
                
                <Text style={styles.emailText}>{email}</Text>

                <TextInput
                  style={[
                    styles.codeInput,
                    verificationCode.length === 5 && styles.codeInputComplete
                  ]}
                  value={verificationCode}
                  onChangeText={handleCodeChange}
                  placeholder="00000"
                  keyboardType="numeric"
                  maxLength={5}
                  autoFocus
                  editable={!loading}
                />

                <View style={styles.codeActions}>
                  <Button
                    mode="contained"
                    onPress={handleVerifyCode}
                    disabled={loading || verificationCode.length !== 5}
                    loading={loading}
                    style={styles.verifyButton}
                    labelStyle={styles.verifyButtonLabel}
                  >
                    {loading ? 'Verificando...' : 'Verificar Código'}
                  </Button>

                  {resendCountdown > 0 ? (
                    <Text style={styles.resendText}>
                      Reenviar código em {resendCountdown}s
                    </Text>
                  ) : (
                    <TouchableOpacity onPress={handleResendCode} disabled={loading}>
                      <Text style={styles.resendLink}>Reenviar código</Text>
                    </TouchableOpacity>
                  )}
                </View>

                <Text style={styles.codeHelp}>
                  {verificationCode.length === 5 
                    ? "Código será verificado automaticamente..." 
                    : "Digite os 5 dígitos. O código será enviado automaticamente ao completar."
                  }
                </Text>

                <TouchableOpacity 
                  onPress={() => setShowCodeInput(false)}
                  style={styles.backButton}
                  disabled={loading}
                >
                  <Text style={styles.backButtonText}>← Voltar para lista de membros</Text>
                </TouchableOpacity>
              </ScrollView>
            ) : (
              // Lista de membros
              <ScrollView 
                style={styles.membersScrollView}
                showsVerticalScrollIndicator={true}
              >
                <View style={styles.membersSection}>
                  <Text style={styles.membersTitle}>Membros do AVAQualis</Text>
                  <Text style={styles.membersSubtitle}>
                    Como visitante você não pode enviar dados para o servidor.{'\n'}
                    Se você é membro do AVAQualis, selecione seu nome abaixo para fazer login.
                  </Text>

                  {AVALIADORES.filter(av => !av.isSpecial).map((avaliador) => (

                    <TouchableOpacity
                      key={avaliador.id}
                      onPress={() => handleRequestCode(avaliador)}
                      disabled={loading}
                      style={styles.memberCard}
                    >
                      <View style={styles.memberContent}>
                        <View style={styles.memberAvatar}>
                          <Text style={styles.memberAvatarText}>
                            {avaliador.nome.charAt(0)}
                          </Text>
                        </View>
                        <View style={styles.memberInfo}>
                          <Text style={styles.memberName}>
                            {formatAvaliadorName(avaliador.nome)}
                          </Text>
                          <View style={styles.memberMeta}>
                            <Text style={styles.memberLevel}>
                              {getLevelEmoji(avaliador.level)} {getLevelDescription(avaliador.level)}
                            </Text>
                          </View>
                        </View>
                        <MaterialCommunityIcons
                          name="chevron-right"
                          size={24}
                          color="#CCCCCC"
                        />
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            )}

            {loading && (
              <View style={styles.loadingOverlay}>
                <ActivityIndicator size="large" color="#A4C639" />
              </View>
            )}
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
};

const styles = {
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  keyboardView: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: windowHeight * 0.9,
    minHeight: windowHeight * 0.5,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    backgroundColor: '#FFFFFF',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: AVAQualisTheme.colors.primary,
  },
  closeButton: {
    margin: 0,
  },
  membersScrollView: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  membersSection: {
    padding: 16,
    paddingBottom: 32,
  },
  membersTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: AVAQualisTheme.colors.primary,
    marginBottom: 8,
  },
  membersSubtitle: {
    fontSize: 14,
    color: '#666666',
    marginBottom: 20,
    lineHeight: 20,
  },
  memberCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    marginBottom: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },
  memberContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  memberAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: AVAQualisTheme.colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  memberAvatarText: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: 'bold',
  },
  memberInfo: {
    flex: 1,
  },
  memberName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333333',
    marginBottom: 4,
  },
  memberMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  memberLevel: {
    fontSize: 13,
    color: '#666666',
  },
  codeSection: {
    alignItems: 'center',
    padding: 24,
    backgroundColor: '#FFFFFF',
  },
  codeIcon: {
    marginBottom: 24,
  },
  codeTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: AVAQualisTheme.colors.primary,
    marginBottom: 16,
    textAlign: 'center',
  },
  codeDescription: {
    fontSize: 16,
    color: '#666666',
    marginBottom: 8,
    textAlign: 'center',
  },
  emailText: {
    fontSize: 16,
    fontWeight: '600',
    color: AVAQualisTheme.colors.primary,
    marginBottom: 32,
    textAlign: 'center',
  },
  codeInput: {
    width: 200,
    height: 60,
    fontSize: 32,
    fontWeight: 'bold',
    textAlign: 'center',
    borderWidth: 2,
    borderColor: AVAQualisTheme.colors.primary,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    letterSpacing: 8,
    marginBottom: 32,
  },
  codeInputComplete: {
    borderColor: '#4CAF50',
    backgroundColor: '#F8FFF8',
  },
  codeActions: {
    alignItems: 'center',
    width: '100%',
  },
  verifyButton: {
    minWidth: 200,
    marginBottom: 16,
    backgroundColor: AVAQualisTheme.colors.primary,
  },
  verifyButtonLabel: {
    fontSize: 16,
    fontWeight: '600',
  },
  resendText: {
    fontSize: 14,
    color: '#999999',
    marginBottom: 8,
  },
  resendLink: {
    fontSize: 14,
    color: AVAQualisTheme.colors.primary,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  codeHelp: {
    fontSize: 13,
    color: '#666666',
    textAlign: 'center',
    marginTop: 24,
    paddingHorizontal: 24,
    lineHeight: 20,
  },
  backButton: {
    marginTop: 20,
    padding: 10,
  },
  backButtonText: {
    fontSize: 14,
    color: AVAQualisTheme.colors.primary,
    fontWeight: '600',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
};

export default LoginModal;